import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { Button, ArrowBackIcon } from "@verily-src/react-design-system";
import { StudyParticipant } from "generated/studyparticipant/studyparticipant_pb";
import {
  CreateParticipantSessionRequest,
  ListParticipantSessionsRequest,
  ParticipantSession,
} from "generated/participantsession/participantsession_pb";
import { ParticipantSessionServiceApiClient } from "apiclient/ParticipantSessionServiceApiClient";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { RpcError, StatusCode } from "grpc-web";
import { ParseIdFromName } from "common/ResourceName";
import { Interval } from "generated/google/type/interval_pb";
import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import WatchAssignmentField, {
  WatchAssignmentFieldRef,
} from "./WatchAssignmentField";
import ConfirmAssignWatchDialog from "./ConfirmAssignWatchDialog";
import { focusOnRef, sxFocusWithin } from "common/Accessibility";
import { useNavigate } from "react-router-dom";
import {
  FocusOn,
  selectStudyParticipant,
  setFocusTarget,
  setSuccessMessage,
} from "./StudyParticipantsStateSlice";
import { AppRoutes } from "core/AppRoutes";
import SDPErrorSnackbar from "components/SDPErrorSnackbar";
import TopAppBar from "components/TopAppBar";
import { telemetry } from "core/TelemetryController";

// The confirmation dialog for unassigning a watch
const AssignWatch: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appState = useAppSelector((state) => state.appState);

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  const exitPage = useCallback(() => {
    dispatch(setFocusTarget(FocusOn.ManageWatch));
    navigate(AppRoutes.SITE_PARTICIPANT);
  }, [dispatch, navigate]);

  const [studyParticipant, setStudyParticipant] = useState<StudyParticipant>(
    new StudyParticipant()
  );

  const participantsViewState = useAppSelector(
    (state) => state.participantsViewState
  );

  // Populates the study participant from store
  useEffect(() => {
    if (participantsViewState.studyParticipantSerialized.length > 0) {
      const serialized = selectStudyParticipant(participantsViewState);
      setStudyParticipant(serialized);
    }
  }, [participantsViewState]);

  const auth0Config = useAppSelector((state) => state.auth0Config);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [mostRecentAssociation, setMostRecentAssociation] = useState<
    ParticipantSession | undefined
  >(undefined);

  const [validateLoading, setValidateLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [
    showConfirmWatchAssignmentDialog,
    setShowConfirmWatchAssignmentDialog,
  ] = useState(false);

  // Error snackbar fields
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");

  const setError = useCallback((message: string) => {
    setErrorSnackbarMessage(message);
    setShowErrorSnackbar(true);
  }, []);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const watchAssignmentRef = useRef<WatchAssignmentFieldRef>(null);

  // Set initial state when the dialog is opened (empty dependency array)
  useEffect(() => {
    setSelectedDeviceId("");
    setMostRecentAssociation(undefined);
    setValidateLoading(false);
    setConfirmLoading(false);
    focusOnRef(titleRef);
  }, []);

  const createParticipantSession = useCallback(
    async (
      participant: StudyParticipant,
      deviceId: string,
      startTime?: Date
    ) => {
      if (!isAuthenticated || !auth0Config) {
        setError("User not authenticated. Please logout and login again.");
        return;
      }

      await (async () => {
        try {
          const token = await getAccessTokenSilently({
            audience: auth0Config.audience,
          });

          const participantSessionApiClient =
            new ParticipantSessionServiceApiClient(
              auth0Config.audience!,
              token
            );

          const studyParticipantId = ParseIdFromName(participant.getName());
          const startTimestamp = new Timestamp();
          if (startTime) {
            startTimestamp.fromDate(startTime);
          } else {
            startTimestamp.fromDate(new Date());
          }

          await participantSessionApiClient.createParticipantSession(
            new CreateParticipantSessionRequest().setParticipantSession(
              new ParticipantSession()
                .setName(`participantSessions/${studyParticipantId}`)
                .setDeviceId(deviceId)
                .setInterval(new Interval().setStartTime(startTimestamp))
            )
          );

          setSelectedDeviceId("");
          dispatch(setSuccessMessage("Watch assigned"));
          exitPage();
        } catch (error) {
          let errMsg = "Failed to assign watch. Please try again.";
          if (
            error instanceof RpcError &&
            error.code === StatusCode.ALREADY_EXISTS
          ) {
            errMsg = `Device ${deviceId} already associated to a participant`;
          } else if (
            error instanceof RpcError &&
            error.code === StatusCode.PERMISSION_DENIED
          ) {
            errMsg =
              "User is not authorized to assign a watch to this participant.";
          }

          telemetry.logError(error);
          setError(errMsg);
        }
      })();
    },
    [
      auth0Config,
      getAccessTokenSilently,
      isAuthenticated,
      dispatch,
      exitPage,
      setError,
    ]
  );

  const getMostRecentAssociation = useCallback(
    async (
      participant: StudyParticipant,
      deviceId: string
    ): Promise<ParticipantSession | undefined> => {
      if (!isAuthenticated || !auth0Config) {
        setError("User not authenticated. Please logout and login again.");
        return undefined;
      }

      const session = await (async () => {
        try {
          const token = await getAccessTokenSilently({
            audience: auth0Config.audience,
          });

          const participantSessionApiClient =
            new ParticipantSessionServiceApiClient(
              auth0Config.audience!,
              token
            );

          const studyParticipantId = ParseIdFromName(participant.getName());
          const response =
            await participantSessionApiClient.listParticipantSessionHistory(
              new ListParticipantSessionsRequest().setParent(
                `studyParticipants/${studyParticipantId}`
              )
            );

          const participantSessionHistory = response.getSessionsList();

          // Most recent association should be first entry
          // List is sorted by start time in descending order
          if (
            participantSessionHistory.length > 0 &&
            participantSessionHistory[0].getDeviceId() === deviceId
          ) {
            return participantSessionHistory[0];
          }

          return undefined;
        } catch (error) {
          telemetry.logError(error);
          let errMsg = "Failed to assign watch. Please try again.";
          if (
            error instanceof RpcError &&
            error.code === StatusCode.PERMISSION_DENIED
          ) {
            errMsg =
              "User is not authorized to assign a watch to this participant.";
          }

          setError(errMsg);
          return undefined;
        }
      })();

      return session;
    },
    [auth0Config, getAccessTokenSilently, isAuthenticated, setError]
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100vh",
        }}
      >
        <TopAppBar scrollTarget={containerRef.current ?? undefined}>
          <Toolbar
            disableGutters
            sx={{
              background: theme.palette.background.canvas,
              justifyContent: "space-between",
              paddingY: "24px",
              paddingX: "40px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Box sx={{ marginRight: "12px", ...sxFocusWithin }}>
                <IconButton onClick={exitPage} aria-label="Back">
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              <Typography
                component="h2"
                ref={titleRef}
                variant="display5"
                tabIndex={-1}
                sx={sxFocusWithin}
              >
                Assign watch
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button
                label="Cancel"
                color="primary"
                variant="outlined"
                onClick={exitPage}
                sx={{
                  marginRight: "8px",
                }}
              />
              <Button
                label="Save"
                variant="filled"
                loading={validateLoading}
                disabled={validateLoading}
                onClick={async () => {
                  setValidateLoading(true);
                  const isDeviceValid =
                    watchAssignmentRef.current!.validateDeviceTextField();
                  if (isDeviceValid) {
                    await watchAssignmentRef.current!.searchDevices();
                  }
                  setValidateLoading(false);
                }}
              />
            </Box>
          </Toolbar>
        </TopAppBar>
        <Container
          disableGutters
          role="main"
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            alignItems: "center",
            padding: "48px 0px 24px",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              width: "464px !important",
            }}
          >
            <WatchAssignmentField
              ref={watchAssignmentRef}
              registryId={studyParticipant.getRegistryId()}
              onSuccess={async () => {
                const deviceId = watchAssignmentRef.current!.fetchDevice();
                setSelectedDeviceId(deviceId);
                setMostRecentAssociation(
                  await getMostRecentAssociation(studyParticipant, deviceId)
                );
                setShowConfirmWatchAssignmentDialog(true);
              }}
            />
          </Box>
        </Container>
      </Box>
      <ConfirmAssignWatchDialog
        studyParticipant={studyParticipant}
        mostRecentAssociation={mostRecentAssociation}
        selectedDeviceId={selectedDeviceId}
        confirmLoading={confirmLoading}
        setConfirmLoading={setConfirmLoading}
        showConfirmWatchAssignmentDialog={showConfirmWatchAssignmentDialog}
        setShowConfirmWatchAssignmentDialog={
          setShowConfirmWatchAssignmentDialog
        }
        createParticipantSession={createParticipantSession}
        watchAssignmentRef={watchAssignmentRef}
      />
      <SDPErrorSnackbar
        showSnackbar={showErrorSnackbar}
        setShowSnackbar={setShowErrorSnackbar}
        errorMessage={errorSnackbarMessage}
      />
    </>
  );
};

export default AssignWatch;
